import OrderApi from '@apis/order/OrderApi';
import cartQueries from '@apis/queries/cartQueries';
import couponQueries from '@apis/queries/couponQueries';
import discountQueries from '@apis/queries/discountQueries';
import giftBoxQueries from '@apis/queries/giftBoxQueries';
import orderLogQueries from '@apis/queries/orderLogQueries';
import orderQueries from '@apis/queries/orderQueries';
import pointQueries from '@apis/queries/pointQueries';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import userQueries from '@apis/queries/userQueries';
import { SAVE_ABLE_PAYMENT_METHODS } from '@constants/orders';
import { isIos } from '@constants/os';
import useKiccEasyCard from '@hooks/easyCard/useKiccEasyCard';
import useAppScheme from '@hooks/native/useAppScheme';
import useFingerPay from '@hooks/order/useFingerPay';
import useNicePay from '@hooks/order/useNicePay';
import usePayU from '@hooks/order/usePayU';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { CreateOrderRequest, CreateOrderResponse } from '@models/order';
import { useFlow } from '@stackflow/react';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQueryClient } from '@tanstack/react-query';
import { setIsPrepaidCardBalanceUpdated } from '@utils/prepaidCardUtils';
import { saveTempOrder } from '@utils/restoreOrderUtils';
import { AxiosError } from 'axios';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import useTossPg from './useTossPg';

function usePrepareOrder() {
    const state = useCalculatedRequest();
    const projectId = useProjectId();
    const project = useProject();
    const [_, setCookie] = useCookies(['shopId', 'shopName', 'tempOrder']);
    const toast = useToast();
    const { push, pop } = useFlow();
    const queryClient = useQueryClient();
    const { startLoading, stopLoading } = useLoading();
    const openFingerPay = useFingerPay();
    const openPayU = usePayU();
    const openNicePay = useNicePay();
    const { t } = useTranslation('order');
    const { handleTossPg } = useTossPg();
    const appScheme = useAppScheme();
    const isUseKiccEasyCard = useKiccEasyCard();
    const isKiccEasyCardPayment = isUseKiccEasyCard && state.paymentMethod === 'EASY_CARD';

    const handleKicc = (checkoutUrl: string) => {
        setCookie('shopId', state.shopId, { path: '/' });
        setCookie('shopName', state.shopName, { path: '/' });
        stopLoading();
        window.location.href = checkoutUrl;
    };
    const generateItemName = () => {
        if (state.orderLines.length === 1) {
            return state.orderLines?.[0].itemName;
        }
        return t('pgItemName.andMore', {
            name: state.orderLines?.[0].itemName,
            count: state.orderLines.length - 1,
        });
    };

    const handleFingerPay = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' | 'returnUrl'>>,
    ) => {
        openFingerPay({
            ...response,
            paymentAmount: state.pgAmount,
            itemName: generateItemName(),
            returnUri: response.returnUrl,
        });
        stopLoading();
    };

    const handleNicePay = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' | 'returnUrl'>>,
    ) => {
        openNicePay({
            ...response,
            paymentAmount: state.pgAmount,
            itemName: generateItemName(),
            returnUri: response.returnUrl,
        });
        stopLoading();
    };

    const handleEasyCard = (
        response: Required<Pick<CreateOrderResponse, 'orderNo' | 'mallId' | 'requestedAt' | 'hashString' | 'returnUrl' >>
        &Pick<CreateOrderResponse, 'approvalMallId'>,
    ) => {
        const {
            orderNo, requestedAt, hashString, approvalMallId, returnUrl,
        } = response;
        stopLoading();
        openPayU({
            orderNo,
            requestedAt,
            paymentAmount: state.pgAmount,
            itemName: generateItemName(),
            approvalMallId,
            hashString,
            returnUrl,
        });
    };

    // 요청사항, 결제수단, 현금영수증 정보 저장
    const saveLocalMethods = () => {
        const {
            saveNote, note, savePaymentMethod, paymentMethod,
            saveCashReceipt, cashReceipt,
        } = state;
        if (saveNote && note) {
            window.localStorage.setItem('note', note);
        } else {
            window.localStorage.removeItem('note');
        }

        if (savePaymentMethod) {
            if (SAVE_ABLE_PAYMENT_METHODS.includes(paymentMethod)) {
                window.localStorage.setItem('paymentMethod', paymentMethod);
            }
        } else {
            window.localStorage.removeItem('paymentMethod');
        }

        // 미신청으로 주문하면 저장해 둔 현금영수증 정보는 그대로 둔다
        if (cashReceipt) {
            if (saveCashReceipt) {
                window.localStorage.setItem('cashReceipt', JSON.stringify(cashReceipt));
            } else {
                window.localStorage.removeItem('cashReceipt');
            }
        }
    };

    return async () => {
        // 간편카드로 결제할 금액이 있는데 카드를 고르지 않았으면 먼저 고르게 한다
        if (isKiccEasyCardPayment && state.pgAmount > 0 && !state.easyCardId) {
            push('OrderEasyCardBottomSheet', {});
            return;
        }
        startLoading();
        const request: CreateOrderRequest = {
            ...state,
            returnUrl: `${import.meta.env.VITE_BASE_URL}/purchase/complete`,
            discounts: state.discounts || [],
            orderLines: state.orderLines.filter((it) => it.quantity > 0),
            projectId,
            appScheme,
        };
        await saveTempOrder(state, 'ORDER');
        if (state.paymentMethod === 'TOSS_PAY') {
            request.appScheme = undefined;
        }
        if (state.paymentMethod === 'TOSS_PAY_DIRECT' && !isIos) {
            request.appScheme = undefined;
        }
        try {
            saveLocalMethods();
            // PG 결제 금액이 0원일 경우 pg 미사용
            if (state.pgAmount === 0) {
                const { orderNo } = await OrderApi.createFreeOrder(request);
                queryClient.invalidateQueries({ queryKey: orderQueries._def });
                queryClient.invalidateQueries({ queryKey: userQueries._def });
                queryClient.invalidateQueries({ queryKey: couponQueries._def });
                queryClient.invalidateQueries({ queryKey: orderLogQueries._def });
                queryClient.invalidateQueries({ queryKey: giftBoxQueries._def });
                queryClient.invalidateQueries({ queryKey: userPrepaidCardQueries._def });
                queryClient.invalidateQueries({ queryKey: pointQueries._def });
                queryClient.resetQueries({ queryKey: discountQueries._def });
                queryClient.resetQueries({ queryKey: cartQueries._def });
                if ((state.prepaidCardPayments?.length || 0) > 0) {
                    setIsPrepaidCardBalanceUpdated(true);
                }
                pop(4, { animate: false });
                push('OrderLogsActivity', { duration: 'ALL' }, { animate: false });
                push('OrderLogActivity', { orderNo });
                stopLoading();
            } else {
                const res = await OrderApi.createOrder(request);
                const {
                    checkoutUrl, orderNo, mallId, requestedAt, hashString, approvalMallId, returnUrl,
                } = res;

                if (isKiccEasyCardPayment) {
                    stopLoading();
                    if (orderNo && state.easyCardId) {
                        push('OrderEasyCardPinActivity', { orderNo, cardId: state.easyCardId });
                    } else {
                        console.error('주문 실패 data:', res);
                        toast.error(t('toast.orderError'));
                    }
                } else if (checkoutUrl) {
                    handleKicc(checkoutUrl);
                } else if (project?.pgVendor === 'TOSS' && orderNo) {
                    handleTossPg(orderNo);
                } else if (orderNo && mallId && requestedAt && hashString && returnUrl) {
                    if (state.paymentMethod === 'EASY_CARD') {
                        handleEasyCard({
                            orderNo, mallId, requestedAt, hashString, approvalMallId, returnUrl,
                        });
                    } else if (project?.pgVendor === 'FINGERPAY') {
                        handleFingerPay({
                            orderNo, mallId, requestedAt, hashString, returnUrl,
                        });
                    } else if (project?.pgVendor === 'NICEPAY') {
                        handleNicePay({
                            orderNo, mallId, requestedAt, hashString, returnUrl,
                        });
                    }
                } else {
                    console.error('주문 실패 data:', res);
                    stopLoading();
                    toast.error(t('toast.orderError'));
                }
            }
        } catch (e) {
            const axiosError = e as AxiosError<ErrorResponse>;
            console.error(e);
            stopLoading();
            const code = axiosError.response?.data?.code;
            // 사용자가 직접 해결할 수 있는 코드만 안내하고, 서버/인증 등 내부 오류는 기본 문구로 처리한다
            const messageKey = code ? ORDER_ERROR_MESSAGE_KEYS[code] : undefined;
            toast.error(t(messageKey ? `toast.${messageKey}` : 'toast.orderError'));
        }
    };
}

/** 서버 오류 코드별 안내 문구 키 (order 네임스페이스 toast.*) */
const ORDER_ERROR_MESSAGE_KEYS: Record<string, string> = {
    EMPTY_CART: 'emptyCart',
    NOT_FOUND_CART: 'emptyCart',
    PRICE_MISMATCH: 'invalidPaymentAmount',
    INVALID_PAYMENT_AMOUNT: 'invalidPaymentAmount',
    INVALID_COUPON_COMBINATION: 'invalidCouponCombination',
    INVALID_GOODS_COUPON_FOR_ORDER: 'invalidGoodsCoupon',
    INVALID_USER_COUPON_ITEM: 'invalidCouponItem',
    INVALID_COUPON_NOT_USABLE_ITEM: 'invalidCouponItem',
    INVALID_USER_COUPON_QTY: 'invalidCouponQty',
    INVALID_USER_COUPON_SHOP: 'invalidCouponShop',
    INVALID_USER_COUPON_ORDER_AMOUNT: 'invalidCouponOrderAmount',
    COUPON_DISCOUNT_MISMATCH: 'couponDiscountMismatch',
    NOT_FOUND_EMPLOYEE_DISCOUNT_POLICY: 'employeeDiscountNotFound',
    NOT_FOUND_SHOP: 'shopUnavailable',
    SHOP_NOT_AVAILABLE: 'shopUnavailable',
    NOT_FOUND_SHOP_ITEM: 'itemUnavailable',
    NOT_FOUND_SHOP_OPTION: 'itemUnavailable',
    UNSUPPORTED_EASY_CARD_PG_VENDOR: 'paymentMethodUnavailable',
    UNSUPPORTED_PAYMENT_HANDLER_STRATEGY: 'paymentMethodUnavailable',
    KICC_EASY_PAY_API_FAILED: 'paymentRequestFailed',
    KG_MOBILIANS_API_FAILED: 'paymentRequestFailed',
    TOSS_PAY_API_FAILED: 'paymentRequestFailed',
    TOSS_PAY_TOKEN_NOT_FOUND: 'paymentRequestFailed',
};

export default usePrepareOrder;
